import {connect} from "react-redux";
import {
    getDriverChangeCorrdinateAction,
    updateChangeLocationStatusAction
} from "../../Store/Actions/DriverLog/Action";
import {useEffect, useMemo, useState} from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    IconButton,
    Badge,
    Paper,
    Divider
} from "@material-ui/core";
import CloseIcon from '@material-ui/icons/Close';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import PersonIcon from '@material-ui/icons/Person';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { withRouter } from 'react-router';
import { useTranslation } from "react-i18next";
import { MapContainer, Marker, TileLayer, useMap } from "react-leaflet";
import { getTileUrl } from "../../Utils/tileUrl";

const hasCoords = (coords) =>
    Array.isArray(coords) &&
    coords.length >= 2 &&
    Number.isFinite(Number(coords[0])) &&
    Number.isFinite(Number(coords[1]));

const formatCoords = (coords) => {
    if (!hasCoords(coords)) return "—";
    return `[${Number(coords[0]).toFixed(6)}, ${Number(coords[1]).toFixed(6)}]`;
};

const toCoords = (latitude, longitude) => [Number(latitude), Number(longitude)];

const displayValue = (value) => {
    if (value === null || value === undefined || value === "" || value === "nan") return "—";
    return value;
};

const MapSizeFix = ({ lat, lng }) => {
    const map = useMap();
    useEffect(() => {
        const fix = () => {
            map.invalidateSize();
            map.setView([lat, lng], 15);
        };
        fix();
        const id = window.setTimeout(fix, 250);
        return () => window.clearTimeout(id);
    }, [map, lat, lng]);
    return null;
};

const CoordMap = ({ coords, tileUrl }) => {
    const lat = Number(coords[0]);
    const lng = Number(coords[1]);
    return (
        <MapContainer
            center={[lat, lng]}
            zoom={15}
            style={{ height: 260, width: "100%", borderRadius: 8 }}
            zoomControl={false}
        >
            <TileLayer
                attribution='&amp;copy <a href="http://osm.org/copyright">OpenStreetMap</a>'
                url={tileUrl}
            />
            <Marker position={[lat, lng]} />
            <MapSizeFix lat={lat} lng={lng} />
        </MapContainer>
    );
};

const ChangeLocationByDispatcherWrapper = (props) => {
    const { t } = useTranslation("translation", {
        keyPrefix: "driverLog",
    });

    const isOptimeSuperAdmin = useMemo(()=>props.location.pathname.includes("couper-atmin"),[props.location.pathname])
    const [dialogOpen, setDialogOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const changeDataList = props.driverLogCoordiante?.response ?? [];
    const totalCount = props.driverLogCoordiante?.response?.length ?? 0;
    const isCoordianteChanged = changeDataList.length > 0;
    const isConfigEnabled = props.User?.changedLocationNeedsApproval
    const isUpdatingStatus = !!props.updateChangeLocationStatus?.request;
    const tileUrl = getTileUrl(props.User?.cityName);
    const activeItem = changeDataList[activeIndex];
    const oldCoords = toCoords(activeItem?.latitude, activeItem?.longitude);
    const newCoords = toCoords(activeItem?.newLatitude, activeItem?.newLongitude);

    useEffect(() => {
        setActiveIndex((index) =>
            Math.min(index, Math.max(changeDataList.length - 1, 0))
        );
    }, [changeDataList.length]);

    useEffect(() => {
        if (!isOptimeSuperAdmin && isConfigEnabled) {
            // Initial fetch
            props.getDriverChangeCorrdinate({pageNumber:1_000,pageIndex:1,onlyUnseen:true });

            // Set up interval for fetching every 60 seconds
            const intervalId = setInterval(() => {
                props.getDriverChangeCorrdinate({pageNumber:1_000,pageIndex:1,onlyUnseen:true });
            }, 60000);

            // Cleanup interval on unmount
            return () => clearInterval(intervalId);
        }
    }, [isOptimeSuperAdmin, isConfigEnabled]);

    const handleOpenDialog = () => {
        setActiveIndex(0);
        setDialogOpen(true);
    };

    const showPrevious = () => {
        setActiveIndex((index) => Math.max(index - 1, 0));
    };

    const showNext = () => {
        setActiveIndex((index) => Math.min(index + 1, changeDataList.length - 1));
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
    };

    const handleCoordinateStatus = (approve) => {
        if (!activeItem || isUpdatingStatus) return;
        props.updateChangeLocationStatus({
            planId: activeItem.planId,
            clusterId: activeItem.clusterId,
            updatedCoordsList: [
                {
                    packetId: activeItem.packetId,
                    approve,
                },
            ],
        });
    };

    // Theme colors based on your screenshot
    const theme = {
        background: '#1a2332',
        cardBg: '#232d3f',
        cardHover: '#2a3548',
        primary: '#00bcd4',
        primaryDark: '#0097a7',
        text: '#ffffff',
        textSecondary: '#8b95a7',
        success: '#4caf50',
        error: '#f44336',
        warning: '#ff9800',
        border: '#2c3e50'
    };

    if (isOptimeSuperAdmin) return props.children

    return <>
        {props.children}

        {isCoordianteChanged && (
            <>
                <Badge
                    badgeContent={totalCount}
                    style={{
                        position: 'fixed',
                        bottom: '35px',
                        right: '40px',
                        zIndex: 9999
                    }}
                    color="error"
                    overlap="circle"
                >
                    <Paper
                        onClick={handleOpenDialog}
                        elevation={8}
                        style={{
                            background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.primaryDark} 100%)`,
                            width: "70px",
                            height: "70px",
                            borderRadius: "50%",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.3s ease",
                            border: `2px solid ${theme.primary}`,
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.1)";
                            e.currentTarget.style.boxShadow = `0 6px 30px ${theme.primary}66`;
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                            e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.5)";
                        }}
                    >
                        <LocationOnIcon style={{ color: 'white', fontSize: 30 }} />
                    </Paper>
                </Badge>

                <Dialog
                    open={dialogOpen}
                    onClose={handleCloseDialog}
                    maxWidth="lg"
                    fullWidth
                    PaperProps={{
                        style: {
                            backgroundColor: theme.background,
                            color: theme.text,
                            borderRadius: 12,
                            border: `1px solid ${theme.border}`
                        }
                    }}
                >
                    <DialogTitle style={{
                        background: `linear-gradient(135deg, ${theme.cardBg} 0%, ${theme.background} 100%)`,
                        color: theme.text,
                        borderBottom: `1px solid ${theme.border}`,
                        padding: '16px 24px'
                    }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Box display="flex" alignItems="center" gap={1}>
                                <LocationOnIcon style={{ color: theme.primary }} />
                                <Typography variant="h6" style={{ color: theme.primary, fontWeight: 500 }}>
                                    {t('locationChanges')} ({totalCount})
                                </Typography>
                            </Box>
                            <IconButton
                                onClick={handleCloseDialog}
                                style={{ color: theme.textSecondary }}
                                size="small"
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </DialogTitle>

                    <DialogContent style={{
                        backgroundColor: theme.background,
                        padding: '24px',
                        maxHeight: '78vh',
                        overflowY: 'auto'
                    }}>
                        {activeItem && (
                            <Box display="flex" alignItems="center">
                                <IconButton
                                    onClick={showPrevious}
                                    disabled={activeIndex === 0}
                                    aria-label={t('previous')}
                                    style={{ color: activeIndex === 0 ? theme.border : theme.primary }}
                                >
                                    <ChevronLeftIcon />
                                </IconButton>

                                <Box flex="1" minWidth={0}>
                                    <Paper
                                        style={{
                                            backgroundColor: theme.cardBg,
                                            padding: 16,
                                        }}
                                    >
                                        <Box display="flex" gap={4} flexWrap="wrap">
                                            <Box flex="1" minWidth="200px">
                                                <Box display="flex" alignItems="center" gap={1} mb={1}>
                                                    <PersonIcon style={{ color: theme.primary, fontSize: 18 }} />
                                                    <Typography variant="caption" style={{ color: theme.textSecondary }}>
                                                        {t('driverInfo')}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="body1" style={{ color: theme.text, fontWeight: 'bold', marginLeft: 26 }}>
                                                    {displayValue(activeItem.driverName)}
                                                </Typography>
                                            </Box>

                                            <Box flex="1" minWidth="160px">
                                                <Typography variant="caption" style={{ color: theme.textSecondary }}>
                                                    {t('pinNumber')}
                                                </Typography>
                                                <Typography variant="body1" style={{ color: theme.text, fontWeight: 'bold' }}>
                                                    {displayValue(activeItem.pinNumber)}
                                                </Typography>
                                            </Box>

                                            <Box flex="1" minWidth="160px">
                                                <Typography variant="caption" style={{ color: theme.textSecondary }}>
                                                    {t('polyNumber')}
                                                </Typography>
                                                <Typography variant="body1" style={{ color: theme.text, fontWeight: 'bold' }}>
                                                    {displayValue(activeItem.polygonNumber)}
                                                </Typography>
                                            </Box>

                                            <Box flex="1" minWidth="160px">
                                                <Typography variant="caption" style={{ color: theme.textSecondary }}>
                                                    {t('orderId')}
                                                </Typography>
                                                <Typography variant="body1" style={{ color: theme.text, fontWeight: 'bold' }}>
                                                    {displayValue(activeItem.orderId)}
                                                </Typography>
                                            </Box>

                                            <Box flex="1" minWidth="180px">
                                                <Typography variant="caption" style={{ color: theme.textSecondary }}>
                                                    {t('customerName')}
                                                </Typography>
                                                <Typography variant="body1" style={{ color: theme.text, fontWeight: 'bold' }}>
                                                    {displayValue(activeItem.customerName)}
                                                </Typography>
                                                <Typography variant="caption" style={{ color: theme.textSecondary }}>
                                                    {displayValue(activeItem.customerPhoneNumber)}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Box width="100%" mt={2}>
                                            <Typography variant="caption" style={{ color: theme.textSecondary }}>
                                                {t('planName')}
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                style={{ color: theme.text, fontWeight: 'bold', wordBreak: 'break-word' }}
                                            >
                                                {displayValue(activeItem.planName)}
                                            </Typography>
                                        </Box>

                                        <Box width="100%" mt={2}>
                                            <Typography variant="caption" style={{ color: theme.textSecondary }}>
                                                {t('address')}
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                style={{ color: theme.text, fontWeight: 'bold', wordBreak: 'break-word' }}
                                            >
                                                {displayValue(activeItem.address)}
                                            </Typography>
                                        </Box>

                                        <Divider style={{ backgroundColor: theme.border, margin: '16px 0' }} />

                                        <Box display="flex" style={{ gap: 16 }}>
                                            <Box flex="1" minWidth={0}>
                                                <Box display="flex" alignItems="center" gap={1} mb={1}>
                                                    <LocationOnIcon style={{ color: theme.warning, fontSize: 18 }} />
                                                    <Typography variant="caption" style={{ color: theme.warning }}>
                                                        {t('oldCoordinates')}
                                                    </Typography>
                                                </Box>
                                                <Typography
                                                    variant="body2"
                                                    style={{
                                                        color: theme.text,
                                                        fontFamily: 'monospace',
                                                        marginBottom: 8,
                                                        backgroundColor: theme.background,
                                                        padding: '4px 8px',
                                                        borderRadius: 4,
                                                        display: 'inline-block'
                                                    }}
                                                >
                                                    {formatCoords(oldCoords)}
                                                </Typography>
                                                {hasCoords(oldCoords) && (
                                                    <CoordMap
                                                        key={`old-${activeIndex}`}
                                                        coords={oldCoords}
                                                        tileUrl={tileUrl}
                                                    />
                                                )}
                                            </Box>

                                            <Box flex="1" minWidth={0}>
                                                <Box display="flex" alignItems="center" gap={1} mb={1}>
                                                    <LocationOnIcon style={{ color: theme.success, fontSize: 18 }} />
                                                    <Typography variant="caption" style={{ color: theme.success }}>
                                                        {t('newCoordinates')}
                                                    </Typography>
                                                </Box>
                                                <Typography
                                                    variant="body2"
                                                    style={{
                                                        color: theme.text,
                                                        fontFamily: 'monospace',
                                                        marginBottom: 8,
                                                        backgroundColor: theme.background,
                                                        padding: '4px 8px',
                                                        borderRadius: 4,
                                                        display: 'inline-block'
                                                    }}
                                                >
                                                    {formatCoords(newCoords)}
                                                </Typography>
                                                {hasCoords(newCoords) && (
                                                    <CoordMap
                                                        key={`new-${activeIndex}`}
                                                        coords={newCoords}
                                                        tileUrl={tileUrl}
                                                    />
                                                )}
                                            </Box>
                                        </Box>

                                        <Box display="flex" justifyContent="flex-end" mt={2} style={{ gap: 12 }}>
                                            <Button
                                                onClick={() => handleCoordinateStatus(false)}
                                                disabled={isUpdatingStatus}
                                                variant="outlined"
                                                style={{
                                                    borderColor: theme.error,
                                                    color: theme.error,
                                                    textTransform: 'none'
                                                }}
                                            >
                                                {t('decline')}
                                            </Button>
                                            <Button
                                                onClick={() => handleCoordinateStatus(true)}
                                                disabled={isUpdatingStatus}
                                                variant="contained"
                                                style={{
                                                    background: theme.success,
                                                    color: theme.text,
                                                    textTransform: 'none'
                                                }}
                                            >
                                                {t('accept')}
                                            </Button>
                                        </Box>
                                    </Paper>

                                    <Box display="flex" justifyContent="center" alignItems="center" mt={2} style={{ gap: 16 }}>
                                        <Button
                                            onClick={showPrevious}
                                            disabled={activeIndex === 0}
                                            variant="outlined"
                                            style={{
                                                borderColor: theme.border,
                                                color: activeIndex === 0 ? theme.border : theme.text,
                                                textTransform: 'none'
                                            }}
                                        >
                                            {t('previous')}
                                        </Button>
                                        <Typography style={{ color: theme.textSecondary }}>
                                            {activeIndex + 1} / {totalCount}
                                        </Typography>
                                        <Button
                                            onClick={showNext}
                                            disabled={activeIndex >= changeDataList.length - 1}
                                            variant="contained"
                                            style={{
                                                background: activeIndex >= changeDataList.length - 1
                                                    ? theme.border
                                                    : `linear-gradient(135deg, ${theme.primary} 0%, ${theme.primaryDark} 100%)`,
                                                color: theme.text,
                                                textTransform: 'none'
                                            }}
                                        >
                                            {t('next')}
                                        </Button>
                                    </Box>
                                </Box>

                                <IconButton
                                    onClick={showNext}
                                    disabled={activeIndex >= changeDataList.length - 1}
                                    aria-label={t('next')}
                                    style={{
                                        color: activeIndex >= changeDataList.length - 1 ? theme.border : theme.primary
                                    }}
                                >
                                    <ChevronRightIcon />
                                </IconButton>
                            </Box>
                        )}
                    </DialogContent>

                    <DialogActions style={{
                        backgroundColor: theme.background,
                        padding: 16,
                        borderTop: `1px solid ${theme.border}`
                    }}>
                        <Button
                            onClick={handleCloseDialog}
                            variant="outlined"
                            style={{
                                borderColor: theme.border,
                                color: theme.textSecondary,
                                textTransform: 'none'
                            }}
                        >
                            {t('close')}
                        </Button>
                    </DialogActions>
                </Dialog>
            </>
        )}
    </>
}

const mapStateToProps = (state) => {
    return {
        driverLogCoordiante: state.driverLog.getDriverLogCorrdinate,
        updateChangeLocationStatus: state.driverLog.updateChangeLocationStatus,
        User: state.auth.User.response,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        getDriverChangeCorrdinate: (data) => {
            dispatch(getDriverChangeCorrdinateAction(data));
        },
        updateChangeLocationStatus: (data) => {
            dispatch(updateChangeLocationStatusAction(data));
        },
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ChangeLocationByDispatcherWrapper))